// The visual sell shown when a free user taps the camera — BEFORE the paywall
// (owner decision). A black stage where a phone mockup runs the real flow on a
// loop: meal photo → scan sweep → ingredient labels float in as the line
// passes them → a nutrition card with example values for that same meal.
// One button; the existing Paywall owns everything about the purchase.
import { useRef } from "react";
import { createPortal } from "react-dom";
import { useEdgeSwipe } from "../lib/useEdgeSwipe";
import { useT } from "../lib/i18n";
import { Mascot } from "./Mascot";
import scanMealUrl from "../assets/scan-meal.jpg";

// Ingredient labels pinned over their spot in the photo. Each carries its own
// keyframe class so the pop stays synced to where the scan line is (the three
// keyframes fire top-to-bottom in photo order — see .pfi-chip--1/2/3).
const CHIPS = [
  { key: "feature.demo.rice", cls: "pfi-chip--1", style: { left: "10%", top: "27%" } },
  { key: "feature.demo.chicken", cls: "pfi-chip--2", style: { left: "8%", top: "48%" } },
  { key: "feature.demo.avocado", cls: "pfi-chip--3", style: { right: "9%", top: "57%" } },
] as const;

/** The looping phone demo: photo → scan → labels → nutrition card. Pure CSS
 *  transforms on an 8s master clock; every element animates with the same
 *  duration so the loop never drifts apart. */
function ScanDemo() {
  const t = useT();
  return (
    <div className="pfi-phone" aria-hidden="true">
      <div className="pfi-screen">
        {/* Camera-style viewfinder: the photo lives in a 3:4 frame like the
            real camera UI, so a near-square shot is never stretched to fill
            the tall screen — the bowl stays a circle. */}
        <div className="pfi-finder">
          <img src={scanMealUrl} alt="" className="pfi-photo" draggable={false} />
          <div className="pfi-scan" />
          {CHIPS.map((c) => (
            <span key={c.key} className={`pfi-chip ${c.cls}`} style={c.style}>
              {t(c.key)}
            </span>
          ))}
        </div>
        <div className="pfi-shutter" />
        <div className="pfi-card">
          <div className="pfi-card-top">
            <span className="pfi-card-name">{t("feature.demo.meal")}</span>
            <span className="pfi-card-kcal">620 kcal</span>
          </div>
          <div className="pfi-card-macros">
            <span><i style={{ background: "rgb(var(--accent-protein))" }} />{t("macro.protein")} 42 g</span>
            <span><i style={{ background: "rgb(var(--accent-move))" }} />{t("macro.carbs")} 58 g</span>
            <span><i style={{ background: "rgb(var(--warn))" }} />{t("macro.fat")} 22 g</span>
          </div>
          <div className="pfi-card-portion">{t("feature.demo.portion")}</div>
        </div>
      </div>
      <div className="pfi-island" />
    </div>
  );
}

export function PremiumFeatureIntro({ onUnlock, onClose }: {
  onUnlock: () => void;
  onClose: () => void;
}) {
  const t = useT();
  // iOS back-gesture closes the intro like every other full-screen surface.
  const rootRef = useRef<HTMLDivElement>(null);
  useEdgeSwipe(rootRef, { onCommit: onClose });
  // Portal to <body>: rendered inside a page that carries animate-rise's
  // transform, a bare `fixed` would anchor to that transformed ancestor
  // instead of the viewport and fail to cover the screen.
  return createPortal(
    <div ref={rootRef} className="fixed inset-0 z-50 bg-black text-white flex flex-col overflow-y-auto">
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.dismiss")}
        className="absolute right-3 w-11 h-11 flex items-center justify-center text-white/70 press z-10"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
      >
        ✕
      </button>

      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-safe gap-7 text-center">
        <ScanDemo />
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">{t("feature.photo.title")}</h1>
          <p className="text-white/60 leading-relaxed max-w-xs mx-auto">{t("feature.photo.sub")}</p>
        </div>
      </div>

      <div className="px-6 pb-safe pb-6 pt-2">
        <button
          type="button"
          onClick={onUnlock}
          className="w-full h-14 rounded-full bg-brand text-on-brand text-base font-semibold press flex items-center justify-center gap-2"
        >
          <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0">
            <Mascot pose="happy" size={24} />
          </span>
          {t("feature.unlock")}
        </button>
      </div>
    </div>,
    document.body,
  );
}
